/**
 * «Ответ по пунктам» (`open_items`, решение владельца 13.10.2026; договор `block-types-1.1.2.md` §8).
 *
 * Открытое задание без правильного ответа: пункты с подсказкой, ученик пишет по строке на пункт,
 * проверяет куратор. Ответ — JSON-массив строк по пунктам.
 *
 * ⚠️ ПРАВИЛО «ЗАПОЛНЕНО» — ДВОЙНИК СЕРВЕРНОГО (`progress.open_items_answer_empty`, миграция 000027):
 * массив, строк не меньше, чем пунктов, в каждой есть буква или цифра. Разойдутся — ученик увидит
 * «Отправить» доступной, а сервер ответит 400 `open_items_empty`, или раздел не закроется.
 */

export type OpenItem = { text?: string; hint?: string; sample?: string };
export type OpenItemsContent = { instruction?: string; items?: OpenItem[] };

/** Предел пунктов — тот же, что у сторожа courses (`проверитьПункты`). */
export const OPEN_ITEMS_MAX = 30;

const hasWord = (s: unknown): boolean => typeof s === 'string' && /[\p{L}\p{N}]/u.test(s);

/** Пункты задания; мусор вместо массива — пусто. */
export function openItemsOf(content: unknown): OpenItem[] {
  const items = (content as OpenItemsContent | null | undefined)?.items;
  if (!Array.isArray(items)) return [];
  return items.map((it) => (it && typeof it === 'object' ? it : {}));
}

/** Заготовка: ни у одного пункта нет текста (`progress.open_items_prompt_empty`). Раздел её не требует. */
export function openItemsPromptEmpty(content: unknown): boolean {
  return !openItemsOf(content).some((it) => hasWord(it.text));
}

/** Ответ строками по числу пунктов: недостающие — пустые, лишние отбрасываются. */
export function parseOpenItemsAnswer(raw: string | null | undefined, count: number): string[] {
  let arr: unknown = null;
  try {
    arr = raw ? JSON.parse(raw) : null;
  } catch {
    arr = null;
  }
  const list = Array.isArray(arr) ? arr : [];
  return Array.from({ length: count }, (_, i) => (typeof list[i] === 'string' ? (list[i] as string) : ''));
}

/** Годен ли ответ — ровно как `progress.open_items_answer_empty` с обратным знаком. */
export function openItemsAnswerOk(content: unknown, raw: string | null | undefined): boolean {
  let arr: unknown;
  try {
    arr = raw ? JSON.parse(raw) : null;
  } catch {
    return false;
  }
  if (!Array.isArray(arr)) return false;
  if (arr.length < openItemsOf(content).length) return false;
  return arr.every(hasWord);
}

/** Сколько пунктов заполнено — для подписи «Заполнено N из M». */
export function openItemsFilled(values: readonly string[]): number {
  return values.filter(hasWord).length;
}

/** Ответ для куратора и «Скопировать»: «1. …» построчно, как `progress.open_items_text`. */
export function openItemsAnswerText(raw: string | null | undefined): string | null {
  let arr: unknown;
  try {
    arr = raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
  if (!Array.isArray(arr) || !arr.some(hasWord)) return null;
  return arr.map((v, i) => `${i + 1}. ${typeof v === 'string' && v.trim() !== '' ? v.trim() : '—'}`).join('\n');
}
